/* eslint-disable @typescript-eslint/no-explicit-any */
export type WSMessageHandler = (msg: any) => void;
export type WSErrorHandler = (err: Event) => void;
export type WSCloseHandler = (ev: CloseEvent) => void;

interface WebSocketClientOptions {
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectInterval?: number;
  idleTimeoutMs?: number;
  heartbeatIntervalMs?: number;
  maxReconnectAttempts?: number;
}

export class WebSocketClient {
  private url: string;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private messageQueue: string[] = [];
  private messageHandlers: WSMessageHandler[] = [];
  private errorHandlers: WSErrorHandler[] = [];
  private closeHandlers: WSCloseHandler[] = [];

  private options: Required<WebSocketClientOptions>;
  private channel = new BroadcastChannel("app-ws");
  private isMaster = false;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastPong = Date.now();

  constructor(url: string, options?: WebSocketClientOptions) {
    this.url = url;
    this.options = {
      reconnect: options?.reconnect ?? true,
      reconnectInterval: options?.reconnectInterval ?? 1000,
      maxReconnectInterval: options?.maxReconnectInterval ?? 10000,
      idleTimeoutMs: options?.idleTimeoutMs ?? 10 * 60 * 1000,
      heartbeatIntervalMs: options?.heartbeatIntervalMs ?? 60 * 1000,
      maxReconnectAttempts: options?.maxReconnectAttempts ?? 3,
    };

    //listen from broadcast channel
    this.channel.onmessage = (ev) => {
      try {
        const data =
          typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        this.messageHandlers.forEach((cb) => cb(data));
      } catch (err) {
        console.error("Broadcast parse error:", err);
      }
    };

    this.becomeMasterIfNeeded();
  }

  private becomeMasterIfNeeded() {
    const existing = localStorage.getItem("ws-master");
    if (!existing) {
      this.isMaster = true;
      localStorage.setItem("ws-master", String(Date.now()));
      window.addEventListener("beforeunload", () => {
        if (this.isMaster) {
          localStorage.removeItem("ws-master");
        }
      });
      this.connect();
    } else {
      this.isMaster = false;
    }
  }

  private connect() {
    if (!this.isMaster) return;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      console.warn("socket connected");
      this.reconnectAttempts = 0;
      this.flushQueue();
      this.startIdleTracking();
      // this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      let data: any;
      try {
        data = JSON.parse(event.data);
      } catch {
        data = event.data;
      }
      if (data?.type === "pong" || data?.type === "echo") {
        this.lastPong = Date.now();
        return;
      }

      this.messageHandlers.forEach((cb) => cb(data));
      this.channel.postMessage(data);
    };

    this.ws.onerror = (event) => {
      this.errorHandlers.forEach((cb) => cb(event));
    };

    this.ws.onclose = (event) => {
      // this.stopHeartbeat();
      this.closeHandlers.forEach((cb) => cb(event));
      if (this.options.reconnect && this.isMaster) {
        this.reconnect();
      }
    };
  }

  private flushQueue() {
    while (
      this.messageQueue.length > 0 &&
      this.ws?.readyState === WebSocket.OPEN
    ) {
      this.ws.send(this.messageQueue.shift()!);
    }
  }

  private reconnect() {
    this.reconnectAttempts++;
    if (this.reconnectAttempts > this.options.maxReconnectAttempts) {
      console.warn(
        `WebSocket: reached max reconnect attempts (${this.options.maxReconnectAttempts}), stopping...`
      );
      return;
    }

    const delay = Math.min(
      this.options.reconnectInterval * this.reconnectAttempts,
      this.options.maxReconnectInterval
    );

    console.warn(`WebSocket: retry #${this.reconnectAttempts} in ${delay}ms`);
    setTimeout(() => this.connect(), delay);
  }

  private startIdleTracking() {
    const reset = () => {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        console.warn("⏸ Idle too long → closing WS");
        this.close();
      }, this.options.idleTimeoutMs);
    };

    ["mousemove", "keydown", "click", "touchstart"].forEach((ev) =>
      window.addEventListener(ev, reset)
    );
    reset();
  }

  // private startHeartbeat() {
  //   this.stopHeartbeat();
  //   this.heartbeatTimer = setInterval(() => {
  //     if (this.ws?.readyState === WebSocket.OPEN) {
  //       this.send({ type: "ping" });
  //       if (Date.now() - this.lastPong > this.options.heartbeatIntervalMs * 3) {
  //         console.warn("No pong → force reconnect");
  //         this.ws?.close();
  //       }
  //     }
  //   }, this.options.heartbeatIntervalMs);
  // }

  // private stopHeartbeat() {
  //   if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
  // }

  public send(data: any) {
    const msg = typeof data === "string" ? data : JSON.stringify(data);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else {
      this.messageQueue.push(msg);
    }
  }

  public onMessage(handler: WSMessageHandler) {
    this.messageHandlers.push(handler);
  }

  public onError(handler: WSErrorHandler) {
    this.errorHandlers.push(handler);
  }

  public onClose(handler: WSCloseHandler) {
    this.closeHandlers.push(handler);
  }

  public close() {
    this.options.reconnect = false;
    // this.stopHeartbeat();
    this.ws?.close();
    if (this.isMaster) {
      localStorage.removeItem("ws-master");
    }
  }
}
